import type { StyleRule } from '@archipro-design/aria';
import { pxToRem } from '@archipro-design/aria';
import { ROW_GAP_MOBILE } from '@modules/page-builder/config/column-constants';
import {
    PADDING_LEFT_RIGHT_SECTION,
    PADDING_LEFT_RIGHT_SECTION_MOBILE,
} from '@modules/page-builder/component/common/pagebuilder.style';

export const TrioImageModuleContainer: StyleRule<{
    isProjectRevamp?: boolean;
}> = ({ theme, isProjectRevamp }) => ({
    display: 'flex',
    flexFlow: 'column',
    alignItems: 'start',
    width: '100%',
    padding: `0 ${pxToRem(PADDING_LEFT_RIGHT_SECTION_MOBILE)}`,

    [theme.screen.laptop]: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        columnGap: pxToRem(32),
        gridTemplateRows: `1fr`,
        width: '100%',
        maxWidth: '100%',
        margin: '0 auto',
        justifyItems: 'center',
        alignItems: 'start',
        padding: `0 ${pxToRem(PADDING_LEFT_RIGHT_SECTION)}`,

        // Project detail revamp (BC-4725): the trio is a centred box capped
        // at the Figma 114.4rem (1830px on the 1920 frame) and otherwise
        // spanning the gallery section content width, so its edges stay flush
        // with the neighbouring modules at every viewport — no gutter
        // overhang (an overhang scales badly in the 744-1024px mobile-root
        // band and reads as the module sticking out past the page edge).
        // Images stretch to their 1fr tracks so the three columns plus the
        // two 3rem gaps always sum to the box width, shrinking the images
        // rather than overflowing. The module's own 32px side padding is
        // dropped (the section gutter already covers it).
        ...(isProjectRevamp && {
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            justifyItems: 'stretch',
            columnGap: '3rem',
            padding: '0',
            maxWidth: '114.4rem',
        }),
    },
});

export const TrioImageContainerImage: (isMedium: boolean) => StyleRule<{
    columns: number;
    mobile: boolean;
    isProjectRevamp?: boolean;
}> =
    (isMedium) =>
    ({ columns, mobile, theme, isProjectRevamp }) => {
        // Project detail revamp: the wrappers stretch to their grid tracks so
        // the fill-width image shells tile to the capped module box — auto
        // margins would win over the grid's `justify-items: stretch` and
        // collapse the wrapper to its (circular) fit-content width of 0.
        const revampTrack = {
            margin: 0,
            width: '100%',
            '&:has(.isMedium)': {
                margin: 0,
            },
        };

        if (isMedium && !mobile) {
            return {
                margin: '0 auto',
                ...(isProjectRevamp && {
                    [theme.screen.laptop]: revampTrack,
                }),
            };
        }

        if (mobile) {
            return Object.assign({
                margin: '0 auto',
                '&:last-child':
                    columns > 1
                        ? {
                              margin: `${pxToRem(ROW_GAP_MOBILE)} auto 0`,
                          }
                        : undefined,
            });
        }
        return {
            margin: `${pxToRem(ROW_GAP_MOBILE)} auto 0`,
            [theme.screen.laptop]: {
                margin: '0 auto',
                '&:has(.isMedium)': {
                    margin: '0 auto',
                },
                ...(isProjectRevamp && revampTrack),
            },
        };
    };
